import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAtom, useSetAtom } from 'jotai'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from '../button'
import { CollapsiblePanel } from '../collapsible-panel'
import { useIsMobile } from 'src/hooks/use-mobile'
import { filterPaneOpenAtom, filterPaneDirectionAtom } from 'src/stores/atom/filter-pane'

interface FilterPaneProps {
  title: string
  children: React.ReactNode
  /** Resets the filters; omit when the pane holds no filters, which hides Clear All Filters. */
  onClear?: () => void
  defaultOpen?: boolean
  className?: string
}

// SVG Chevron icon matching SidebarSection style
const ChevronIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
)

const ChevronLeftIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
)

const ChevronRightIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
)

export const FilterPane: React.FC<FilterPaneProps> = ({
  title,
  children,
  onClear,
  defaultOpen = true,
  className = 'bg-transparent',
}) => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [, setFilterPaneOpen] = useAtom(filterPaneOpenAtom)
  const setDirection = useSetAtom(filterPaneDirectionAtom)
  const direction = isMobile ? 'vertical' : 'horizontal'

  React.useEffect(() => {
    setDirection(direction)
  }, [direction, setDirection])

  return (
    <CollapsiblePanel
      title={title}
      defaultOpen={defaultOpen}
      direction={direction}
      className={`${
        isMobile ? 'mb-4 w-full' : ''
      } rounded-xl border border-border bg-surface shadow-sm`}
      headerClassName="bg-surface"
      contentClassName={className}
      collapsedWidth="5rem"
      expandedWidth="24rem"
      showBorder={false}
      collapseIcon={isMobile ? <ChevronIcon /> : <ChevronLeftIcon />}
      expandIcon={isMobile ? <ChevronIcon /> : <ChevronRightIcon />}
      onToggle={setFilterPaneOpen}
    >
      <div className="space-y-4">
        {children}
        {onClear && (
          <Button
            variant="ghost"
            className="mt-4 w-full"
            icon={<XMarkIcon />}
            onClick={onClear}
            data-testid="clear-all-filters"
          >
            {t('shared.clearAllFilters')}
          </Button>
        )}
      </div>
    </CollapsiblePanel>
  )
}
